import { isValidHandoffCodeShape } from '@/features/auth/lib/handoff-code'
import { IdentityAuthPath } from '@/features/auth/lib/constants'

/** Bound handoff so a down `/api/v1/auth/handoff/use` proxy cannot block SPA mount forever. */
export const PROVIDER_HANDOFF_FETCH_TIMEOUT_MS = 20_000

const HANDOFF_SESSION_VERIFY_ATTEMPTS = 3
const HANDOFF_SESSION_VERIFY_GAP_MS = 40

function handoffAbortSignal(): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(PROVIDER_HANDOFF_FETCH_TIMEOUT_MS)
  }
  const c = new AbortController()
  globalThis.setTimeout(() => c.abort(), PROVIDER_HANDOFF_FETCH_TIMEOUT_MS)
  return c.signal
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms)
  })
}

async function verifyHandoffSession(): Promise<boolean> {
  for (let attempt = 0; attempt < HANDOFF_SESSION_VERIFY_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await delay(HANDOFF_SESSION_VERIFY_GAP_MS)
    } else {
      await delay(0)
    }
    try {
      const meUrl = new URL(IdentityAuthPath.ME, globalThis.location.origin)
      const meRes = await fetch(meUrl, {
        method: 'GET',
        credentials: 'include',
        signal: handoffAbortSignal(),
      })
      if (meRes.ok) {
        return true
      }
    } catch {
      // retry
    }
  }
  return false
}

/**
 * BFP-1004 — If the URL carries `_handoff` from marketing global sign-in,
 * exchange it for host-only session cookies before the app tree mounts.
 * Always strips `_handoff` from the address bar when present.
 */
export async function consumeProviderHandoffFromLocation(): Promise<void> {
  const params = new URLSearchParams(globalThis.location.search)
  const handoffCode = params.get('_handoff')
  if (!handoffCode) return

  if (isValidHandoffCodeShape(handoffCode)) {
    try {
      const handoffUrl = new URL(
        '/api/v1/auth/handoff/use',
        globalThis.location.origin,
      )
      handoffUrl.searchParams.set('code', handoffCode)
      const response = await fetch(handoffUrl, {
        method: 'POST',
        credentials: 'include',
        signal: handoffAbortSignal(),
      })
      if (response.ok) {
        await verifyHandoffSession()
      }
    } catch {
      // Leave cookies unset; RequireAuth will send the user to /login.
    }
  }

  params.delete('_handoff')
  const cleanPath =
    globalThis.location.pathname +
    (params.size > 0 ? `?${params.toString()}` : '')
  globalThis.history.replaceState(null, '', cleanPath)
}
